import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

// Cache the per-business email (Gmail) connection result so the many customer
// cards that render at once share a single lookup.
const emailConnectedCache = new Map<string, boolean>();

/**
 * Reports which customer messaging channels are connected for the current business:
 *  • twilioConnected — drives in-app Dialler (call) and SMS. Read synchronously from the business.
 *  • emailConnected  — drives the in-app email composer (Gmail). Looked up once and cached.
 * Components fall back to native tel:/sms:/mailto: when the relevant channel isn't connected.
 */
export function useMessagingChannels(): { twilioConnected: boolean; emailConnected: boolean } {
  const { selectedBusiness } = useBusiness();
  const biz = selectedBusiness as any;

  const twilioConnected = !!(
    biz?.twilio_enabled &&
    biz?.twilio_account_sid &&
    biz?.twilio_auth_token &&
    biz?.VITE_TWILIO_PHONE_NUMBER
  );

  const businessId: string | undefined = biz?.id;
  const [emailConnected, setEmailConnected] = useState<boolean>(
    businessId ? emailConnectedCache.get(businessId) ?? false : false,
  );

  useEffect(() => {
    if (!businessId || !supabase) return;
    if (emailConnectedCache.has(businessId)) {
      setEmailConnected(emailConnectedCache.get(businessId)!);
      return;
    }
    let active = true;
    supabase
      .from('gmail_connections')
      .select('id')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .limit(1)
      .then(({ data }) => {
        const connected = (data?.length ?? 0) > 0;
        emailConnectedCache.set(businessId, connected);
        if (active) setEmailConnected(connected);
      });
    return () => { active = false; };
  }, [businessId]);

  return { twilioConnected, emailConnected };
}
